import { useEffect, useState } from "react";
import { Button } from "@/components/common/Button";
import { Input } from "@/components/common/Input";
import { Modal } from "@/components/common/Modal";
import type { SelectOption } from "@/components/common/Select";
import { Select } from "@/components/common/Select";
import { useInsurers } from "@/hooks/useSettlements";
import type {
  ApprovalPolicyLine,
  ManualEntryRequest,
} from "@/types/settlement";

const MANUAL_ENTRY_TYPES: {
  value: string;
  label: string;
  enumValue: number;
}[] = [
  { value: "Policy", label: "Polisa", enumValue: 0 },
  { value: "Bonus", label: "Premia", enumValue: 1 },
  { value: "Correction", label: "Korekta", enumValue: 2 },
  { value: "Other", label: "Inny", enumValue: 3 },
];

function getEntryTypeFlags(type: string) {
  const isPolicy = type === "Policy";
  const isBonus = type === "Bonus";
  const isCorrection = type === "Correction";

  return {
    isPolicy,
    isBonus,
    isCorrection,
    showPolicyNumber: !isBonus,
    policyNumberRequired: isPolicy,
    showPremium: !isBonus,
    noteRequired: !isPolicy,
    commissionLabel: isBonus
      ? "Kwota premii"
      : isCorrection
        ? "Kwota korekty"
        : isPolicy
          ? "Prowizja"
          : "Kwota",
  };
}

interface ManualEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ManualEntryRequest) => void;
  isLoading: boolean;
  editingPolicy: ApprovalPolicyLine | null;
}

export function ManualEntryModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  editingPolicy,
}: ManualEntryModalProps) {
  const { data: insurers } = useInsurers();

  const [insurerId, setInsurerId] = useState("");
  const [manualEntryType, setManualEntryType] = useState("Policy");
  const [policyNumber, setPolicyNumber] = useState("");
  const [premiumAmount, setPremiumAmount] = useState("");
  const [commissionAmount, setCommissionAmount] = useState("");
  const [note, setNote] = useState("");
  const [productName, setProductName] = useState("");
  const [clientName, setClientName] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate fields when editing
  useEffect(() => {
    if (editingPolicy) {
      const matchedInsurer = insurers?.find(
        (i) => i.name === editingPolicy.insurerName,
      );
      setInsurerId(matchedInsurer?.id ?? "");
      setManualEntryType(editingPolicy.manualEntryType ?? "Policy");
      setPolicyNumber(editingPolicy.policyNumber);
      setPremiumAmount(String(editingPolicy.premiumAmount));
      setCommissionAmount(String(editingPolicy.commissionAmount));
      setNote(editingPolicy.manualEntryNote ?? "");
      setProductName(editingPolicy.productName ?? "");
      setClientName(editingPolicy.clientName ?? "");
    } else {
      setInsurerId("");
      setManualEntryType("Policy");
      setPolicyNumber("");
      setPremiumAmount("");
      setCommissionAmount("");
      setNote("");
      setProductName("");
      setClientName("");
    }
    setErrors({});
  }, [editingPolicy, insurers]);

  const insurerOptions: SelectOption[] = (insurers ?? []).map((i) => ({
    value: i.id,
    label: i.name,
  }));

  const typeOptions: SelectOption[] = MANUAL_ENTRY_TYPES.map((t) => ({
    value: t.value,
    label: t.label,
  }));

  const flags = getEntryTypeFlags(manualEntryType);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!insurerId) {
      newErrors.insurerId = "Wybierz ubezpieczyciela";
    }
    if (flags.policyNumberRequired && !policyNumber.trim()) {
      newErrors.policyNumber = "Numer polisy jest wymagany";
    }
    if (!commissionAmount.trim() || Number.isNaN(Number(commissionAmount))) {
      newErrors.commissionAmount = "Podaj poprawną kwotę";
    }
    if (flags.showPremium && premiumAmount.trim() && Number.isNaN(Number(premiumAmount))) {
      newErrors.premiumAmount = "Podaj poprawną kwotę składki";
    }
    if (flags.noteRequired && !note.trim()) {
      newErrors.note = flags.isBonus
        ? "Podaj za co premia"
        : flags.isCorrection
          ? "Podaj co jest korygowane"
          : "Komentarz jest wymagany";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    const typeEntry = MANUAL_ENTRY_TYPES.find(
      (t) => t.value === manualEntryType,
    );

    const data: ManualEntryRequest = {
      insurerId,
      policyNumber: flags.showPolicyNumber ? policyNumber.trim() : "",
      premiumAmount: flags.showPremium && premiumAmount ? Number(premiumAmount) : 0,
      commissionAmount: Number(commissionAmount),
      manualEntryType: typeEntry?.enumValue ?? 0,
      note: note.trim() || undefined,
      productName: productName.trim() || undefined,
      clientName: clientName.trim() || undefined,
    };

    onSubmit(data);
  }

  const isEditing = editingPolicy !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edytuj wpis ręczny" : "Dodaj wpis ręczny"}
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Ubezpieczyciel"
          options={insurerOptions}
          value={insurerId}
          onChange={setInsurerId}
          placeholder="Wybierz ubezpieczyciela..."
          error={errors.insurerId}
        />

        <Select
          label="Typ wpisu"
          options={typeOptions}
          value={manualEntryType}
          onChange={(val) => {
            setManualEntryType(val);
            setErrors({});
          }}
        />

        {flags.showPolicyNumber && (
          <Input
            label="Numer polisy"
            value={policyNumber}
            onChange={(e) => setPolicyNumber(e.target.value)}
            error={errors.policyNumber}
            required={flags.policyNumberRequired}
            placeholder={
              flags.policyNumberRequired
                ? undefined
                : "Opcjonalne — zostanie wygenerowane automatycznie"
            }
          />
        )}

        <div className={flags.showPremium ? "grid grid-cols-2 gap-4" : ""}>
          {flags.showPremium && (
            <Input
              label="Składka"
              type="number"
              step="0.01"
              value={premiumAmount}
              onChange={(e) => setPremiumAmount(e.target.value)}
              error={errors.premiumAmount}
            />
          )}

          <Input
            label={flags.commissionLabel}
            type="number"
            step="0.01"
            value={commissionAmount}
            onChange={(e) => setCommissionAmount(e.target.value)}
            error={errors.commissionAmount}
            required
          />
        </div>

        <Input
          label="Komentarz"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          error={errors.note}
          required={flags.noteRequired}
          placeholder={
            flags.isBonus
              ? "Za co premia?"
              : flags.isCorrection
                ? "Co jest korygowane?"
                : undefined
          }
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Nazwa produktu"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
          />

          <Input
            label="Nazwa klienta"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" isLoading={isLoading} disabled={isLoading}>
            {isEditing ? "Zapisz zmiany" : "Dodaj wpis"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
